/**
 * The node. Everything above this file is mechanism; this is the loop.
 *
 * A node is a node whether it is a browser tab with the wallet open, a
 * Cloudflare Durable Object, or a laptop running `npm run node`. They all
 * run this class, speak the same sealed protocol, and carry the same
 * weight in a poll. There is no validator class to apply to, no stake to
 * post, no allow-list to be removed from.
 */

import { fromHex, randomBytes, toHex } from './bytes.ts';
import { Dag } from './dag.ts';
import { Consensus } from './snowball.ts';
import { Ledger } from './state.ts';
import { adaptive, params, shardOf, type ChainParams } from './params.ts';
import {
  buildVertex, challengeForBeacon, conflictKey, senderAddress, validateStateless,
  type MintBody, type SignedVertex, type TransferBody, type VertexId,
} from './tx.ts';
import { RelayDirectory, type RelayDescriptor, type Transport } from './routing.ts';
import type { DeviceAssertion, DeviceCredential } from './webauthn.ts';
import { MAX_IDS_PER_MESSAGE, MAX_VERTICES_PER_MESSAGE, type InnerMessage } from './wire.ts';
import { addressFromPublicKey, keyPairFromSeed, type KeyPair } from './keys.ts';
import { LedgerStore, MemoryKv, type KvStore } from './store.ts';
import { formatFace, halvingEpoch, invitesRemaining, mintRewardAt } from './mint.ts';

export interface PeerLink {
  readonly id: string;
  readonly transport: Transport;
  readonly url?: string;
  send(msg: InnerMessage): void;
  close(reason?: string): void;
  isOpen(): boolean;
}

export interface EngineEvents {
  vertex: (v: SignedVertex) => void;
  accepted: (v: SignedVertex) => void;
  rejected: (v: SignedVertex, reason: string) => void;
  peers: (count: number) => void;
  status: (line: string) => void;
  balance: (address: string, grains: bigint) => void;
}

export interface EngineOptions {
  seed: Uint8Array;
  chainParams?: ChainParams;
  kv?: KvStore;
  relays?: RelayDirectory;
  /** Set false in read-only viewers such as the explorer. */
  participateInConsensus?: boolean;
  /** Wall clock, injectable for tests. */
  now?: () => number;
  /**
   * Separate 32-byte seed for the transport identity. Deliberately not the
   * wallet key: relays necessarily learn which node ids are online at the
   * same time, and that metadata must not be linkable to an address that
   * holds money. Defaults to a fresh random identity per session.
   */
  nodeSeed?: Uint8Array;
  /**
   * Key for at-rest encryption of the replica. Deliberately separate from
   * the wallet seed: the replica belongs to the *device*, and tying it to
   * a wallet key would make the cache unreadable the moment a wallet is
   * restored, rotated or adopted. Defaults to the wallet seed for callers
   * that never swap identity.
   */
  storeKey?: Uint8Array;
}

interface PendingQuery {
  resolve: (answers: Record<VertexId, boolean>) => void;
  timer: ReturnType<typeof setTimeout>;
}

export class Engine {
  /**
   * Wallet identity: signs vertices, owns the balance. Swappable, because
   * a node should be running and syncing before the user has decided
   * whether to create a wallet at all - the network wants the participant
   * either way, and the liveness beacon needs a live frontier to draw from.
   */
  private walletKeys: KeyPair;
  /** Transport identity: used for peer handshakes only, rotated per session. */
  readonly nodeKeys: KeyPair;
  readonly chainParams: ChainParams;
  readonly dag: Dag;
  readonly ledger: Ledger;
  readonly consensus: Consensus;
  readonly relays: RelayDirectory;
  readonly store: LedgerStore;

  private peers = new Map<string, PeerLink>();
  private orphans = new Map<VertexId, SignedVertex>();
  private orphanWaiters = new Map<VertexId, Set<VertexId>>();
  private applyQueue: SignedVertex[] = [];
  private pendingQueries = new Map<number, PendingQuery>();
  private ridCounter = 1;
  private listeners: { [K in keyof EngineEvents]?: Set<EngineEvents[K]> } = {};
  private roundTimer: ReturnType<typeof setInterval> | null = null;
  private recentBySender = new Map<string, number[]>();
  private now: () => number;
  private participate: boolean;
  private txThisSecond = 0;
  private tpsWindow: number[] = [];
  private lastTpsTick = 0;
  private running = false;

  constructor(opts: EngineOptions) {
    this.chainParams = opts.chainParams ?? params();
    this.walletKeys = keyPairFromSeed(opts.seed);
    this.nodeKeys = keyPairFromSeed(opts.nodeSeed ?? randomBytes(32));
    this.dag = new Dag(this.chainParams);
    this.ledger = new Ledger(this.chainParams);
    this.consensus = new Consensus(this.dag, (v) => this.ledger.check(v).ok || this.ledger.wasApplied(v.id), 1);
    this.relays = opts.relays ?? new RelayDirectory();
    this.store = new LedgerStore(opts.kv ?? new MemoryKv(), opts.storeKey ?? opts.seed);
    this.now = opts.now ?? (() => Date.now());
    this.participate = opts.participateInConsensus !== false;
  }

  get keys(): KeyPair {
    return this.walletKeys;
  }

  /** Adopt a wallet key without tearing down the node or losing the DAG. */
  setWalletIdentity(seed: Uint8Array): void {
    this.walletKeys = keyPairFromSeed(seed);
  }

  /* ----------------------------- events ---------------------------- */

  on<K extends keyof EngineEvents>(event: K, fn: EngineEvents[K]): () => void {
    let set = this.listeners[event] as Set<EngineEvents[K]> | undefined;
    if (!set) (this.listeners[event] as unknown) = set = new Set();
    set.add(fn);
    return () => set!.delete(fn);
  }

  private emit<K extends keyof EngineEvents>(event: K, ...args: Parameters<EngineEvents[K]>): void {
    const set = this.listeners[event] as Set<(...a: unknown[]) => void> | undefined;
    if (!set) return;
    for (const fn of set) {
      try {
        fn(...(args as unknown[]));
      } catch (e) {
        console.error('[engine] listener threw', e);
      }
    }
  }

  /* --------------------------- lifecycle --------------------------- */

  async start(): Promise<void> {
    if (this.running) return;
    this.running = true;
    await this.store.load();
    await this.store.replay(async (v) => {
      // Locally persisted history is already ours; re-verify signatures on
      // replay anyway, because a corrupted or tampered replica must not be
      // able to inject state a node never actually agreed to.
      const res = validateStateless(v, { now: this.now(), chainParams: this.chainParams });
      if (!res.ok) return;
      this.dag.insert(v, 'accepted', v.ts);
      this.consensus.track(v);
      this.applyQueue.push(v);
    });
    this.drainApplyQueue();
    if (this.participate) {
      this.scheduleRounds();
    }
    this.emit('status', `node ${this.keys.address} ready - ${this.dag.size} vertices replayed`);
  }

  stop(): void {
    this.running = false;
    if (this.roundTimer) clearInterval(this.roundTimer);
    this.roundTimer = null;
    for (const p of this.peers.values()) p.close('shutting down');
    this.peers.clear();
  }

  private scheduleRounds(): void {
    if (this.roundTimer) clearInterval(this.roundTimer);
    const { roundMs } = this.consensus.params;
    this.roundTimer = setInterval(() => {
      void this.consensusRound();
    }, roundMs);
  }

  /* ----------------------------- peers ----------------------------- */

  attachPeer(link: PeerLink): void {
    if (this.peers.has(link.id)) {
      this.peers.get(link.id)!.close('replaced by a newer link');
    }
    this.peers.set(link.id, link);
    this.consensus.setNodeCount(this.networkSize());
    this.scheduleRounds();
    this.emit('peers', this.peers.size);

    // Introduce ourselves: our frontier and the relays we know work.
    link.send({ t: 'tips', rid: this.nextRid() });
    link.send({ t: 'peers', relays: this.relays.select(16) });
    const recent = this.dag.entriesList().slice(-64).map((e) => e.vertex.id);
    if (recent.length) link.send({ t: 'have', ids: recent });
  }

  detachPeer(id: string, reason = 'closed'): void {
    const link = this.peers.get(id);
    if (!link) return;
    this.peers.delete(id);
    this.consensus.setNodeCount(this.networkSize());
    this.emit('peers', this.peers.size);
    this.emit('status', `peer ${id.slice(0, 8)} disconnected (${reason})`);
  }

  peerList(): PeerLink[] {
    return [...this.peers.values()].filter((p) => p.isOpen());
  }

  /** Local peers plus ourselves - the denominator for adaptive parameters. */
  networkSize(): number {
    return this.peers.size + 1;
  }

  /* -------------------------- message intake ------------------------ */

  async handleMessage(peerId: string, msg: InnerMessage): Promise<void> {
    const peer = this.peers.get(peerId);
    if (!peer) return;
    switch (msg.t) {
      case 'ping':
        peer.send({ t: 'pong', ts: msg.ts, nodes: this.networkSize() });
        break;

      case 'pong':
        break;

      case 'have': {
        if (!Array.isArray(msg.ids)) return;
        const unknown = msg.ids.slice(0, MAX_IDS_PER_MESSAGE).filter((id) => !this.dag.has(id) && !this.orphans.has(id));
        if (unknown.length) peer.send({ t: 'want', ids: unknown });
        break;
      }

      case 'want': {
        if (!Array.isArray(msg.ids)) return;
        const out: SignedVertex[] = [];
        for (const id of msg.ids.slice(0, MAX_VERTICES_PER_MESSAGE)) {
          const v = this.dag.vertex(id);
          if (v) out.push(v);
        }
        if (out.length) peer.send({ t: 'vertices', vertices: out });
        break;
      }

      case 'vertices': {
        if (!Array.isArray(msg.vertices)) return;
        for (const v of msg.vertices.slice(0, MAX_VERTICES_PER_MESSAGE)) {
          await this.ingest(v, peerId);
        }
        break;
      }

      case 'query': {
        // Answering a poll is the civic duty of every node; it is also the
        // only thing a node must do to be counted as part of the network.
        const ids = Array.isArray(msg.ids) ? msg.ids.slice(0, 64) : [];
        peer.send({ t: 'qreply', rid: msg.rid, answers: this.consensus.answerQuery(ids) });
        break;
      }

      case 'qreply': {
        const pending = this.pendingQueries.get(msg.rid);
        if (!pending) return;
        clearTimeout(pending.timer);
        this.pendingQueries.delete(msg.rid);
        pending.resolve(msg.answers ?? {});
        break;
      }

      case 'tips':
        peer.send({ t: 'tipsreply', rid: msg.rid, tips: this.dag.tips().slice(0, 256), height: this.store.height });
        break;

      case 'tipsreply': {
        const unknown = (msg.tips ?? []).filter((id) => !this.dag.has(id));
        if (unknown.length) peer.send({ t: 'want', ids: unknown });
        if (msg.height > this.store.height + 8) {
          peer.send({ t: 'sync', rid: this.nextRid(), since: this.store.height, limit: MAX_VERTICES_PER_MESSAGE });
        }
        break;
      }

      case 'sync': {
        const since = Math.max(0, Math.floor(msg.since ?? 0));
        const limit = Math.min(MAX_VERTICES_PER_MESSAGE, Math.max(1, msg.limit ?? 64));
        const vertices = await this.store.range(since, limit);
        peer.send({
          t: 'syncreply', rid: msg.rid, vertices,
          more: since + vertices.length < this.store.height,
          cursor: since + vertices.length,
        });
        break;
      }

      case 'syncreply': {
        for (const v of (msg.vertices ?? []).slice(0, MAX_VERTICES_PER_MESSAGE)) await this.ingest(v, peerId);
        if (msg.more) peer.send({ t: 'sync', rid: this.nextRid(), since: msg.cursor, limit: MAX_VERTICES_PER_MESSAGE });
        break;
      }

      case 'peers': {
        const added = this.relays.merge(msg.relays ?? []);
        if (added) this.emit('status', `learned ${added} new relay${added === 1 ? '' : 's'}`);
        break;
      }
    }
  }

  /* ---------------------------- ingestion --------------------------- */

  /**
   * The single gate every vertex passes through, local or remote. Order
   * matters: cheap structural checks first, signature and work next,
   * state last, so that flooding a node with garbage costs the attacker
   * more than it costs the node.
   */
  async ingest(v: SignedVertex, fromPeer?: string): Promise<{ ok: boolean; reason?: string }> {
    if (!v || typeof v !== 'object' || typeof v.id !== 'string') return { ok: false, reason: 'malformed' };
    if (this.dag.has(v.id) || this.orphans.has(v.id)) return { ok: true };

    const required = this.requiredPowBits(v);
    const stateless = validateStateless(v, { now: this.now(), requiredPowBits: required, chainParams: this.chainParams });
    if (!stateless.ok) {
      this.emit('rejected', v, stateless.reason!);
      return { ok: false, reason: stateless.reason };
    }

    // A vertex may not claim to be older than the frontier it is built on.
    //
    // Timestamps are otherwise the issuer's to choose, and a past one is
    // free: backdate a guarded transfer by a day and its escrow window is
    // already over when it lands, which hands a coercer the money
    // immediately. Bounding it against the parents is deterministic -
    // every node that accepts this vertex holds those parents, and their
    // timestamps are fixed - so no node can disagree about the bound.
    let newestParent = 0;
    for (const p of v.parents) {
      const e = this.dag.get(p);
      if (e && e.vertex.ts > newestParent) newestParent = e.vertex.ts;
    }
    if (newestParent && v.ts < newestParent - this.chainParams.maxBackdateMs) {
      const reason = 'timestamp is older than the vertices it builds on';
      this.emit('rejected', v, reason);
      return { ok: false, reason };
    }

    // Hold vertices whose causal past has not arrived yet, and ask for it.
    const missing = this.dag.missingParents(v);
    if (missing.length) {
      if (this.orphans.size > 4096) return { ok: false, reason: 'orphan buffer full' };
      this.orphans.set(v.id, v);
      for (const m of missing) {
        let set = this.orphanWaiters.get(m);
        if (!set) this.orphanWaiters.set(m, (set = new Set()));
        set.add(v.id);
      }
      if (fromPeer) this.peers.get(fromPeer)?.send({ t: 'want', ids: missing });
      return { ok: true, reason: 'buffered pending parents' };
    }

    this.recordRate(v);
    this.dag.insert(v, 'pending', this.now());
    this.consensus.track(v);
    await this.store.append(v);
    this.emit('vertex', v);
    this.countTx();

    this.gossip({ t: 'have', ids: [v.id] }, fromPeer);
    await this.releaseOrphans(v.id);

    // An uncontested vertex on a network of one has nobody to ask; accept
    // it locally so a single offline node still works, and let the poll
    // reconcile if it later meets a network that disagrees.
    if (this.peers.size === 0 && this.participate) {
      this.selfPoll([v.id]);
    }
    return { ok: true };
  }

  private async releaseOrphans(parentId: VertexId): Promise<void> {
    const waiting = this.orphanWaiters.get(parentId);
    if (!waiting) return;
    this.orphanWaiters.delete(parentId);
    for (const id of waiting) {
      const v = this.orphans.get(id);
      if (!v) continue;
      if (this.dag.missingParents(v).length) continue;
      this.orphans.delete(id);
      await this.ingest(v);
    }
  }

  /**
   * Anti-spam pricing. A sender's first few vertices per minute cost the
   * base difficulty; past that the work required doubles per extra vertex.
   * A normal person paying for lunch never notices. A flooder is priced
   * out in CPU within seconds, and pays in their own electricity rather
   * than in fees taken from users who have none to spare.
   */
  requiredPowBits(v: { from: string; ts: number }): number {
    const p = this.chainParams;
    const recent = this.recentBySender.get(v.from) ?? [];
    const cutoff = this.now() - p.powRateWindowMs;
    const live = recent.filter((t) => t >= cutoff).length;
    const extra = Math.max(0, live - p.powRateFreeTx);
    return Math.min(p.maxPowBits, p.basePowBits + extra);
  }

  private recordRate(v: SignedVertex): void {
    const cutoff = this.now() - this.chainParams.powRateWindowMs;
    const list = (this.recentBySender.get(v.from) ?? []).filter((t) => t >= cutoff);
    list.push(this.now());
    this.recentBySender.set(v.from, list);
    if (this.recentBySender.size > 20_000) {
      for (const [k, times] of this.recentBySender) {
        if (!times.some((t) => t >= cutoff)) this.recentBySender.delete(k);
      }
    }
  }

  private countTx(): void {
    const sec = Math.floor(this.now() / 1000);
    if (sec !== this.lastTpsTick) {
      this.tpsWindow.push(this.txThisSecond);
      if (this.tpsWindow.length > 60) this.tpsWindow.shift();
      this.txThisSecond = 0;
      this.lastTpsTick = sec;
    }
    this.txThisSecond++;
  }

  tps(): number {
    if (!this.tpsWindow.length) return this.txThisSecond;
    const sum = this.tpsWindow.reduce((a, b) => a + b, 0);
    return +(sum / this.tpsWindow.length).toFixed(2);
  }

  private gossip(msg: InnerMessage, exceptPeer?: string): void {
    for (const [id, p] of this.peers) {
      if (id === exceptPeer || !p.isOpen()) continue;
      try {
        p.send(msg);
      } catch {
        this.detachPeer(id, 'send failed');
      }
    }
  }

  /* --------------------------- consensus ---------------------------- */

  private nextRid(): number {
    return this.ridCounter++;
  }

  /**
   * One polling round: sample k peers, ask them about a slice of the
   * undecided frontier, fold the answers in. Sampling is uniform and
   * without replacement, which is what the security analysis assumes.
   */
  async consensusRound(): Promise<void> {
    if (!this.participate) return;
    const pending = this.consensus.pending(24);
    if (!pending.length) return;

    const { k, alpha } = this.consensus.params;
    void k;
    const open = this.peerList();

    if (open.length === 0) {
      // Solo: nothing to sample. Vertices still settle by local validity so
      // an offline wallet keeps working; they re-poll on reconnect.
      this.selfPoll(pending);
      return;
    }

    const sample = shuffle(open).slice(0, Math.min(k, open.length));
    const replies = await Promise.all(sample.map((p) => this.query(p, pending)));

    const tally = new Map<VertexId, { yes: number; asked: number }>();
    for (const id of pending) tally.set(id, { yes: 0, asked: 0 });
    for (const r of replies) {
      if (!r) continue;
      for (const id of pending) {
        const t = tally.get(id)!;
        t.asked++;
        if (r[id]) t.yes++;
      }
    }

    // Scale the quorum to how many peers actually answered. Two rules,
    // and both matter:
    //
    //   - Keep alpha's *ratio* (about 0.72), rounded rather than ceilinged.
    //     Ceilinging forces unanimity at small sample sizes - with three
    //     peers, ceil(0.72 x 3) is 3 - so a single stale or partitioned
    //     peer stalls finality for the whole network. That is a liveness
    //     failure, not a safety measure.
    //   - Never drop below a strict majority of the answers, whatever the
    //     ratio says. Two disjoint subsets must never both reach quorum,
    //     or conflicting transactions could both finalise.
    const answered = replies.filter(Boolean).length;
    const ratioAlpha = Math.round(this.consensus.params.alphaRatio * Math.max(1, answered));
    const majority = Math.floor(Math.max(1, answered) / 2) + 1;
    const effectiveAlpha = Math.max(majority, ratioAlpha);
    this.consensus.params = { ...this.consensus.params, alpha: effectiveAlpha };

    this.consensus.recordPoll([...tally].map(([id, t]) => ({ id, yes: t.yes, asked: t.asked })));
    this.consensus.params = { ...this.consensus.params, alpha };
    this.settleAccepted();
  }

  /**
   * Settle vertices on a node that has nobody to ask.
   *
   * The quorum has to be lowered to one for this, and it is not a
   * formality: alpha is three even on a network of one, so a self-vote of
   * "yes" counts as one out of a required three and nothing is ever
   * accepted. The first person to claim on an empty network would watch
   * their own mint sit pending forever, which is precisely the case a
   * launch consists of.
   */
  private selfPoll(ids: VertexId[]): void {
    if (!ids.length) return;
    const saved = this.consensus.params;
    this.consensus.params = { ...saved, alpha: 1 };
    try {
      this.consensus.recordPoll(ids.map((id) => ({ id, yes: 1, asked: 1 })));
    } finally {
      this.consensus.params = saved;
    }
    this.settleAccepted();
  }

  private query(peer: PeerLink, ids: VertexId[]): Promise<Record<VertexId, boolean> | null> {
    return new Promise((resolve) => {
      const rid = this.nextRid();
      const timer = setTimeout(() => {
        this.pendingQueries.delete(rid);
        this.relays.recordFailure(peer.url ?? peer.id);
        resolve(null);
      }, Math.max(250, this.consensus.params.roundMs * 2));
      this.pendingQueries.set(rid, { resolve: (a) => resolve(a), timer });
      try {
        peer.send({ t: 'query', rid, ids });
      } catch {
        clearTimeout(timer);
        this.pendingQueries.delete(rid);
        resolve(null);
      }
    });
  }

  /** Move newly finalised vertices into ledger state. */
  private settleAccepted(): void {
    for (const e of this.dag.entriesList()) {
      if (e.status !== 'accepted') continue;
      if (this.ledger.wasApplied(e.vertex.id)) continue;
      this.applyQueue.push(e.vertex);
    }
    this.drainApplyQueue();
  }

  /**
   * Apply queued vertices, retrying until no further progress is possible.
   * Nonce ordering is per sender and is not implied by DAG order, so a
   * later nonce can arrive first and simply waits its turn here.
   */
  private drainApplyQueue(): void {
    let progressed = true;
    while (progressed && this.applyQueue.length) {
      progressed = false;
      const remaining: SignedVertex[] = [];
      for (const v of this.applyQueue) {
        if (this.ledger.wasApplied(v.id)) continue;
        const res = this.ledger.apply(v);
        if (res.ok) {
          progressed = true;
          this.dag.setStatus(v.id, 'accepted');
          this.emit('accepted', v);
          const from = senderAddress(v);
          this.emit('balance', from, this.ledger.balance(from));
          if (v.type === 'transfer') {
            const to = (v.body as TransferBody).to;
            this.emit('balance', to, this.ledger.balance(to));
          }
        } else if (res.reason?.includes('nonce out of order')) {
          remaining.push(v); // causally early, will apply once its predecessor lands
        } else {
          this.dag.setStatus(v.id, 'rejected');
          this.emit('rejected', v, res.reason ?? 'invalid');
        }
      }
      this.applyQueue = remaining;
    }
  }

  /* ------------------------- local issuance ------------------------- */

  /**
   * A fresh liveness challenge, bound to a real vertex on the frontier.
   *
   * `avoid` lets the caller demand digits different from the ones just
   * failed. Re-showing the same three digits after a failure would let
   * someone who fumbled the first attempt simply play a recording made
   * during it, and it makes the retry feel broken besides.
   */
  livenessBeacon(avoid?: string): { beacon: VertexId; challenge: string } {
    const tips = this.dag.tips();
    const pool = tips.length ? tips : this.dag.selectTips(1, () => true);
    const shuffled = shuffle(pool);
    for (const candidate of shuffled) {
      const challenge = challengeForBeacon(candidate);
      if (challenge !== avoid) return { beacon: candidate, challenge };
    }
    // Every vertex on a very small frontier yields the same challenge. Say
    // so by returning it anyway rather than looping forever.
    const beacon = shuffled[0] ?? pool[0];
    return { beacon, challenge: challengeForBeacon(beacon) };
  }

  private chooseParents(mustInclude?: VertexId): VertexId[] {
    const want = Math.min(this.chainParams.maxParents, Math.max(this.chainParams.minParents, 2 + Math.floor(Math.log2(this.networkSize() + 1))));
    const chosen = this.dag.selectTips(want, (id) => this.consensus.isStronglyPreferred(id), this.now());
    if (!mustInclude || chosen.includes(mustInclude)) return chosen;
    // Swap the weakest pick for the required one rather than growing the
    // set past maxParents.
    return [...chosen.slice(0, Math.max(this.chainParams.minParents - 1, chosen.length - 1)), mustInclude].sort();
  }

  /**
   * Asks the wallet's authenticator to approve a vertex. Set by the app;
   * absent on relay-only nodes, which never issue anything.
   */
  attest?: (challenge: Uint8Array) => Promise<DeviceAssertion>;

  async submitTransfer(
    to: string,
    amountGrains: bigint,
    opts: { memoHex?: string; onPowProgress?: (n: number) => void } = {},
  ): Promise<SignedVertex> {
    const from = this.keys.address;
    if (amountGrains <= 0n) throw new Error('amount must be greater than zero');
    // Spendable, not settled: money already committed to an in-flight
    // vertex is gone even though the ledger has not caught up yet.
    const available = this.spendable(from);
    if (available < amountGrains) {
      // Say *why* there is nothing to spend. Someone holding 65,536 FACE
      // who is told "0 available" will reasonably conclude their money has
      // vanished, when in fact it is sitting behind the invite unlock.
      const locked = this.ledger.locked(from);
      if (locked > 0n) {
        const left = invitesRemaining(this.ledger.invites(from), this.chainParams);
        throw new Error(
          `${formatFace(available)} FACE available - ${formatFace(locked)} FACE is still locked. ` +
          `Invite ${left} more ${left === 1 ? 'person' : 'people'} to unlock the rest.`,
        );
      }
      throw new Error(`insufficient balance: ${formatFace(available)} FACE available, ${formatFace(amountGrains)} needed`);
    }

    const body: TransferBody = { to, amount: amountGrains.toString(10), memo: opts.memoHex };
    const v = await buildVertex(this.keys.secretKey, this.keys.publicKey, 'transfer', this.pendingNonce(from), body, {
      parents: this.chooseParents(),
      extraPowBits: this.extraPowBits(),
      now: this.now(),
      onPowProgress: opts.onPowProgress,
      chainParams: this.chainParams,
      attest: this.attest,
    });
    return this.issue(v, 'transaction');
  }

  async submitMint(body: Omit<MintBody, 'epoch' | 'amount'>, opts: { onPowProgress?: (n: number) => void } = {}): Promise<SignedVertex> {
    const now = this.now();
    const amount = mintRewardAt(now, this.chainParams);
    if (amount <= 0n) throw new Error('the halving schedule is exhausted - no further FACE can be minted');
    const full: MintBody = { ...body, epoch: halvingEpoch(now, this.chainParams), amount: amount.toString(10) };
    // The beacon the challenge came from has to travel as a parent, so
    // every validator can see the claimant did not invent it.
    const v = await buildVertex(this.keys.secretKey, this.keys.publicKey, 'mint', this.pendingNonce(this.keys.address), full, {
      parents: this.chooseParents(full.liveness.beacon),
      extraPowBits: this.extraPowBits(),
      now,
      onPowProgress: opts.onPowProgress,
      chainParams: this.chainParams,
      attest: this.attest,
    });
    return this.issue(v, 'mint');
  }

  /**
   * Publish a locally built vertex. The state pre-check is what turns
   * "the network quietly ignored your claim" into "this face has already
   * minted" - the wallet needs the reason, not a timeout. Nonce ordering
   * is exempt because a vertex issued while an earlier one is still in
   * flight is legitimately ahead of settled state.
   */
  private async issue(v: SignedVertex, label: string): Promise<SignedVertex> {
    const pre = this.ledger.check(v, { ignoreNonce: true });
    if (!pre.ok && pre.reason !== 'already applied') throw new Error(pre.reason ?? `${label} rejected`);
    const res = await this.ingest(v);
    if (!res.ok) throw new Error(res.reason ?? `${label} rejected by local node`);
    this.gossip({ t: 'vertices', vertices: [v] });
    return v;
  }

  /**
   * Release a matured guarded transfer, or take one back before it matures.
   *
   * Releasing is something the recipient's wallet does for itself once the
   * window closes; cancelling is the sender's, and is the whole reason the
   * window exists.
   */
  async submitGuard(kind: 'settle' | 'cancel', ref: VertexId): Promise<SignedVertex> {
    // The same two verbs resolve both waiting things: a transfer in escrow
    // and an announced device change.
    const waiting = this.ledger.guardedTransfer(ref)
      ?? this.ledger.pendingRotations(this.keys.address).find((r) => r.ref === ref);
    if (!waiting) throw new Error('nothing is waiting under that reference');
    const v = await buildVertex(this.keys.secretKey, this.keys.publicKey, kind,
      this.pendingNonce(this.keys.address), { ref } as never, {
        parents: this.chooseParents(),
        extraPowBits: this.extraPowBits(),
        now: this.now(),
        chainParams: this.chainParams,
        attest: this.attest,
      });
    return this.issue(v, kind);
  }

  /**
   * Announce a new authenticator for this wallet.
   *
   * Takes effect after the change window unless the current device stops
   * it. The assertion must come from the *new* device, so a rotation
   * cannot name a credential its author does not hold.
   */
  async submitRotate(device: DeviceCredential, attestWithNew: (c: Uint8Array) => Promise<DeviceAssertion>): Promise<SignedVertex> {
    const v = await buildVertex(this.keys.secretKey, this.keys.publicKey, 'rotate',
      this.pendingNonce(this.keys.address), { device } as never, {
        parents: this.chooseParents(),
        extraPowBits: this.extraPowBits(),
        now: this.now(),
        chainParams: this.chainParams,
        attest: attestWithNew,
      });
    return this.issue(v, 'device change');
  }

  /** Device changes announced against this wallet, soonest first. */
  pendingRotations(address = this.keys.address) {
    return this.ledger.pendingRotations(address);
  }

  /** Guarded transfers involving this wallet, soonest first. */
  escrow(address = this.keys.address) {
    return this.ledger.guardedFor(address);
  }

  /** Release anything of ours that has matured. Safe to call on a timer. */
  async releaseMatured(): Promise<number> {
    let released = 0;
    for (const g of this.escrow()) {
      if (g.to !== this.keys.address || g.matureAt > this.now()) continue;
      try {
        await this.submitGuard('settle', g.ref);
        released++;
      } catch {
        // Someone else may have released it first; that is fine.
      }
    }
    return released;
  }

  /**
   * Extra proof of work this node currently demands of us.
   *
   * Every issuing path has to ask, not just transfers: a wallet that has
   * been busy is charged more, and a vertex built at the base difficulty
   * is rejected by its own node the moment that happens.
   */
  private extraPowBits(): number {
    return Math.max(0, this.requiredPowBits({ from: this.keys.publicKeyHex, ts: this.now() }) - this.chainParams.basePowBits);
  }

  /** Next nonce including vertices still in flight, so back-to-back sends work. */
  pendingNonce(address: string): number {
    let n = this.ledger.nextNonce(address);
    for (const e of this.dag.entriesList()) {
      if (e.status === 'rejected') continue;
      if (senderAddress(e.vertex) !== address) continue;
      if (this.ledger.wasApplied(e.vertex.id)) continue;
      n = Math.max(n, e.vertex.nonce + 1);
    }
    return n;
  }

  /**
   * What can actually be sent: settled balance, minus the locked airdrop,
   * minus anything already committed to a vertex still in flight.
   */
  spendable(address: string): bigint {
    let b = this.ledger.available(address);
    for (const e of this.dag.entriesList()) {
      if (e.status === 'rejected' || this.ledger.wasApplied(e.vertex.id)) continue;
      if (e.vertex.type !== 'transfer' || senderAddress(e.vertex) !== address) continue;
      b -= BigInt((e.vertex.body as TransferBody).amount);
    }
    return b < 0n ? 0n : b;
  }

  /* ----------------------------- queries ---------------------------- */

  history(address: string, limit = 100): { vertex: SignedVertex; direction: 'in' | 'out' | 'mint'; status: string }[] {
    const out: { vertex: SignedVertex; direction: 'in' | 'out' | 'mint'; status: string }[] = [];
    for (const e of this.dag.entriesList()) {
      const v = e.vertex;
      const from = senderAddress(v);
      if (v.type === 'settle' || v.type === 'cancel') {
        const g = this.ledger.guardedTransfer((v.body as { ref: string }).ref);
        if (from === address || g?.to === address) out.push({ vertex: v, direction: 'out', status: e.status });
        continue;
      }
      if (v.type === 'mint') {
        if (from === address) out.push({ vertex: v, direction: 'mint', status: e.status });
      } else {
        const b = v.body as TransferBody;
        if (from === address) out.push({ vertex: v, direction: 'out', status: e.status });
        else if (b.to === address) out.push({ vertex: v, direction: 'in', status: e.status });
      }
    }
    out.sort((a, b) => b.vertex.ts - a.vertex.ts);
    return out.slice(0, limit);
  }

  networkStats() {
    const a = adaptive(this.networkSize());
    const d = this.dag.stats();
    const c = this.consensus.stats();
    return {
      address: this.keys.address,
      nodeId: this.nodeKeys.publicKeyHex,
      chain: this.chainParams.chainId,
      peers: this.peers.size,
      nodes: this.networkSize(),
      k: a.k, alpha: a.alpha, beta: a.beta, shards: a.shards, roundMs: a.roundMs,
      vertices: d.vertices, tips: d.tips, accepted: d.accepted, pending: d.pending, rejected: d.rejected,
      cumulativeWeight: d.totalWeight,
      conflictSets: c.conflictSets, contested: c.contested,
      humans: this.ledger.humanCount,
      accounts: this.ledger.accountCount,
      supply: this.ledger.totalSupply.toString(),
      lockedSupply: this.ledger.lockedSupply.toString(),
      circulatingSupply: this.ledger.circulatingSupply.toString(),
      tps: this.tps(),
      relays: this.relays.all().length,
      orphans: this.orphans.size,
      height: this.store.height,
      shard: shardOf(this.keys.address, a.shards),
    };
  }

  addressOf(publicKeyHex: string): string {
    return addressFromPublicKey(fromHex(publicKeyHex));
  }
}

function shuffle<T>(arr: T[]): T[] {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

export { toHex, fromHex, conflictKey };
